import React, { memo } from 'react';
import { Box, Switch, Tooltip } from '@mui/material';
import { LIST_TABLE_SX } from '../../mathReports/mathTableStyles';
import { KIND_LABELS, SCHEDULE_LABELS, closeText, extendBlocked, formatDay, formatMoment, ruleText } from './revenueEntryCalc';
import type { EntryData, EntryKind } from './revenueEntryApi';

/** Права на параметры сети; без права кнопка параметра не показывается */
export interface EntryNetworkPerms {
  schedule: boolean;
  extend: boolean;
  unlock: boolean;
  date: boolean;
}

interface EntryParamsTableProps {
  data: EntryData;
  perms: EntryNetworkPerms;
  onEditSchedule: () => void;
  onSetRule: (kind: EntryKind) => void;
  onClearRule: (kind: EntryKind) => void;
}

interface ParamRow {
  key: string;
  label: string;
  value: string;
  actions: React.ReactNode;
}

const EntryParamsTable: React.FC<EntryParamsTableProps> = ({ data, perms, onEditSchedule, onSetRule, onClearRule }) => {
  const { schedule, network, state, bounds } = data;

  const editButton = (title: string, onClick: () => void, disabledHint?: string) => (
    <Tooltip title={disabledHint || title} enterDelay={disabledHint ? 200 : 800} arrow>
      {/* span нужен, чтобы подсказка работала и на отключенной кнопке */}
      <span>
        <button type="button" className="act" disabled={!!disabledHint} onClick={onClick}>
          Изменить
        </button>
      </span>
    </Tooltip>
  );

  const clearButton = (kind: EntryKind) =>
    network[kind] ? (
      <Tooltip title="Отменить раньше срока" enterDelay={800} arrow>
        <button type="button" className="act danger" onClick={() => onClearRule(kind)}>
          Отменить
        </button>
      </Tooltip>
    ) : null;

  const rows: ParamRow[] = [
    {
      key: 'open',
      label: SCHEDULE_LABELS.open,
      value: schedule.open,
      actions: perms.schedule && editButton('Изменить окно ввода', onEditSchedule)
    },
    {
      key: 'close',
      label: SCHEDULE_LABELS.close,
      value: closeText(schedule.open, schedule.close),
      actions: perms.schedule && editButton('Изменить окно ввода', onEditSchedule)
    },
    {
      key: 'extend',
      label: KIND_LABELS.extend,
      value: ruleText('extend', network.extend),
      actions: perms.extend && (
        <>
          {editButton(
            'Продлить ввод для всей сети',
            () => onSetRule('extend'),
            !network.extend && extendBlocked(bounds)
              ? `Продление сейчас невозможно: обычное закрытие ввода (${formatMoment(bounds.extendAfter)}) дальше 24 часов`
              : undefined
          )}
          {clearButton('extend')}
        </>
      )
    },
    {
      key: 'unlock',
      label: KIND_LABELS.unlock,
      value: ruleText('unlock', network.unlock),
      actions: perms.unlock && (
        <>
          {network.unlock && editButton('Изменить срок разблокировки', () => onSetRule('unlock'))}
          <Tooltip title={network.unlock ? 'Снять разблокировку' : 'Разблокировать таблицу для всей сети'} enterDelay={800} arrow>
            <Switch
              size="small"
              checked={!!network.unlock}
              onChange={(event) => (event.target.checked ? onSetRule('unlock') : onClearRule('unlock'))}
              inputProps={{ 'aria-label': KIND_LABELS.unlock }}
              sx={{ ml: 1 }}
            />
          </Tooltip>
        </>
      )
    },
    {
      key: 'date',
      label: KIND_LABELS.date,
      value: network.date ? ruleText('date', network.date) : `${formatDay(state.date)}, автоматически`,
      actions: perms.date && (
        <>
          {editButton('Сменить дату ввода для всей сети', () => onSetRule('date'))}
          {clearButton('date')}
        </>
      )
    }
  ];

  const withActions = perms.schedule || perms.extend || perms.unlock || perms.date;

  return (
    <Box sx={LIST_TABLE_SX}>
      <table>
        <thead>
          <tr>
            <th>Параметр</th>
            <th>Значение</th>
            {withActions && <th className="right actions">Действия</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="muted nowrap">{row.label}</td>
              <td className="strong">{row.value}</td>
              {withActions && (
                <td className="right nowrap">
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    {row.actions || null}
                  </Box>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

export default memo(EntryParamsTable);
